import React, { useEffect, useState } from 'react';
import { Box, Chip, FormControl, MenuItem, Select, SelectChangeEvent, InputLabel, OutlinedInput, Checkbox, ListItemText, TextField, InputAdornment, Tooltip, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';
import { InfoIcon } from 'lucide-react';
import DataTable, { Column, RowAction, FilterChip } from './DataTable';
import { shouldShowResendEmail } from '../utils/passwordResetHistory';
interface SubAgentUser {
  id: number;
  name: string;
  fatherName: string;
  cnic: string;
  email: string;
  phone: string;
  username: string;
  branchCode: string;
  settlementAccount: string;
  subAgent: string;
  status: 'active' | 'inactive';
  userType: 'maker' | 'checker';
  createdAt: string;
}
interface SubAgentUsersTableProps {
  users: SubAgentUser[];
  onEditUser: (user: SubAgentUser) => void;
  searchQuery: string;
  subAgentFilter: string | null;
  onStatusChange?: (user: SubAgentUser, newStatus: 'active' | 'inactive') => void;
  onResetPassword?: (user: SubAgentUser) => void;
  onViewResetHistory?: (user: SubAgentUser) => void;
  onResendEmail?: (user: SubAgentUser) => void;
}
interface SubAgentRecord {
  id: number;
  name: string;
  fatherName: string;
  cnic: string;
  email: string;
  phone: string;
  username: string;
  branchCode: string;
  settlementAccount: string;
  subAgent: string;
  status: 'active' | 'inactive';
  userType: 'maker' | 'checker';
  createdAt: string;
}
interface SubAgentRecordsTableProps {
  records: SubAgentRecord[];
  onEditRecord: (record: SubAgentRecord) => void;
  searchQuery: string;
  subAgentFilter: string | null;
  onStatusChange?: (record: SubAgentRecord, newStatus: 'active' | 'inactive') => void;
  onResetPassword?: (record: SubAgentRecord) => void;
  onViewResetHistory?: (record: SubAgentRecord) => void;
  onResendEmail?: (record: SubAgentRecord) => void;
}
const SUB_AGENTS = ['Paga Ghana', 'MoneyGram Nigeria', 'WorldRemit Kenya', 'Flutterwave Ghana', 'Flutterwave Nigeria', 'M-Pesa Kenya', 'Orange Money Senegal', 'Accra Money Center', 'Lagos Express', 'Nairobi Remit Center'];
const BRANCH_CODES = ['GH-ACC-001', 'GH-KUM-001', 'NG-LAG-001', 'NG-ABJ-001', 'SN-DKR-001', 'UG-KLA-001', 'KE-NBI-001', 'CI-ABJ-001'];
const SubAgentRecordsTable: React.FC<SubAgentRecordsTableProps> = ({
  records,
  onEditRecord,
  searchQuery,
  subAgentFilter,
  onStatusChange,
  onResetPassword,
  onViewResetHistory,
  onResendEmail
}) => {
  const [filters, setFilters] = useState({
    subAgents: subAgentFilter ? [subAgentFilter] : [] as string[],
    branchCodes: [] as string[],
    status: 'all',
    userType: 'all',
    dateFrom: '',
    dateTo: '',
    search: searchQuery || '',
    name: '',
    fatherName: '',
    cnic: '',
    email: '',
    phone: '',
    username: '',
    settlementAccount: ''
  });
  const [loading, setLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState<{
    [key: string]: string | string[];
  }>({});
  const validateDateRange = (from: string, to: string): boolean => {
    if (!from || !to) return true;
    const fromDate = new Date(from);
    const toDate = new Date(to);
    const diffMonths = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());
    return diffMonths <= 3;
  };
  const handleDateChange = (field: 'dateFrom' | 'dateTo', value: string) => {
    const newFilters = {
      ...filters,
      [field]: value
    };
    if (newFilters.dateFrom && newFilters.dateTo && !validateDateRange(newFilters.dateFrom, newFilters.dateTo)) {
      return;
    }
    setFilters(newFilters);
  };
  // Handle column filter change
  const handleColumnFilterChange = (columnId: string, value: string | string[]) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnId]: value
    }));
  };
  // Handle text filter change
  const handleTextFilterChange = (field: string, value: string) => {
    setFilters({
      ...filters,
      [field]: value
    });
  };
  // Update search when searchQuery prop changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      search: searchQuery || ''
    }));
  }, [searchQuery]);
  // Apply filters to the data
  const filteredRecords = records.filter(record => {
    // Apply search query
    if (filters.search) {
      const query = filters.search.toLowerCase();
      if (!record.name.toLowerCase().includes(query) && !record.email.toLowerCase().includes(query) && !record.cnic.toLowerCase().includes(query) && !record.phone.toLowerCase().includes(query) && !record.username.toLowerCase().includes(query) && !record.subAgent.toLowerCase().includes(query) && !record.branchCode.toLowerCase().includes(query)) {
        return false;
      }
    }
    // Column-specific text filters
    if (filters.name && !record.name.toLowerCase().includes(filters.name.toLowerCase())) {
      return false;
    }
    if (filters.fatherName && !record.fatherName.toLowerCase().includes(filters.fatherName.toLowerCase())) {
      return false;
    }
    if (filters.cnic && !record.cnic.toLowerCase().includes(filters.cnic.toLowerCase())) {
      return false;
    }
    if (filters.email && !record.email.toLowerCase().includes(filters.email.toLowerCase())) {
      return false;
    }
    if (filters.phone && !record.phone.toLowerCase().includes(filters.phone.toLowerCase())) {
      return false;
    }
    if (filters.username && !record.username.toLowerCase().includes(filters.username.toLowerCase())) {
      return false;
    }
    if (filters.settlementAccount && !record.settlementAccount.toLowerCase().includes(filters.settlementAccount.toLowerCase())) {
      return false;
    }
    // Sub Agent filter
    if (filters.subAgents.length > 0 && !filters.subAgents.includes(record.subAgent)) {
      return false;
    }
    // Branch code filter
    if (filters.branchCodes.length > 0 && !filters.branchCodes.includes(record.branchCode)) {
      return false;
    }
    // Status filter
    if (filters.status !== 'all' && record.status !== filters.status) {
      return false;
    }
    // User type filter from main filters
    if (filters.userType !== 'all' && record.userType !== filters.userType) {
      return false;
    }
    // User type filter from column filter
    if (columnFilters.userType && columnFilters.userType !== 'all' && record.userType !== columnFilters.userType) {
      return false;
    }
    // Status filter from column filter
    if (columnFilters.status && columnFilters.status !== 'all' && record.status !== columnFilters.status) {
      return false;
    }
    // SubAgent filter from column filter
    if (columnFilters.subAgent && columnFilters.subAgent !== 'all' && record.subAgent !== columnFilters.subAgent) {
      return false;
    }
    // Branch code filter from column filter
    if (columnFilters.branchCode && columnFilters.branchCode !== 'all' && record.branchCode !== columnFilters.branchCode) {
      return false;
    }
    // Date filters
    if (filters.dateFrom) {
      const userDate = new Date(record.createdAt);
      const fromDate = new Date(filters.dateFrom);
      if (userDate < fromDate) {
        return false;
      }
    }
    if (filters.dateTo) {
      const userDate = new Date(record.createdAt);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (userDate > toDate) {
        return false;
      }
    }
    return true;
  });
  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      subAgents: [],
      branchCodes: [],
      status: 'all',
      userType: 'all',
      dateFrom: '',
      dateTo: '',
      search: '',
      name: '',
      fatherName: '',
      cnic: '',
      email: '',
      phone: '',
      username: '',
      settlementAccount: ''
    });
    setColumnFilters({});
  };
  // Handle search change
  const handleSearchChange = (value: string) => {
    setFilters({
      ...filters,
      search: value
    });
  };
  // Define filter chips
  const filterChips: FilterChip[] = [...(filters.name ? [{
    id: 'name',
    label: 'Name',
    value: filters.name,
    onDelete: () => handleTextFilterChange('name', '')
  }] : []), ...(filters.fatherName ? [{
    id: 'fatherName',
    label: 'Department',
    value: filters.fatherName,
    onDelete: () => handleTextFilterChange('fatherName', '')
  }] : []), ...(filters.cnic ? [{
    id: 'cnic',
    label: 'Passport/ID',
    value: filters.cnic,
    onDelete: () => handleTextFilterChange('cnic', '')
  }] : []), ...(filters.email ? [{
    id: 'email',
    label: 'Email',
    value: filters.email,
    onDelete: () => handleTextFilterChange('email', '')
  }] : []), ...(filters.phone ? [{
    id: 'phone',
    label: 'Phone',
    value: filters.phone,
    onDelete: () => handleTextFilterChange('phone', '')
  }] : []), ...(filters.username ? [{
    id: 'username',
    label: 'Username',
    value: filters.username,
    onDelete: () => handleTextFilterChange('username', '')
  }] : []), ...(filters.settlementAccount ? [{
    id: 'settlementAccount',
    label: 'Settlement Account',
    value: filters.settlementAccount,
    onDelete: () => handleTextFilterChange('settlementAccount', '')
  }] : []), ...filters.subAgents.map(subAgent => ({
    id: `subAgent-${subAgent}`,
    label: 'Sub Agent',
    value: subAgent,
    onDelete: () => setFilters({
      ...filters,
      subAgents: filters.subAgents.filter(s => s !== subAgent)
    })
  })), ...filters.branchCodes.map(code => ({
    id: `branchCode-${code}`,
    label: 'Branch',
    value: code,
    onDelete: () => setFilters({
      ...filters,
      branchCodes: filters.branchCodes.filter(c => c !== code)
    })
  })), ...(filters.status !== 'all' ? [{
    id: 'status',
    label: 'Status',
    value: filters.status,
    onDelete: () => setFilters({
      ...filters,
      status: 'all'
    })
  }] : []), ...(filters.userType !== 'all' ? [{
    id: 'userType',
    label: 'User Type',
    value: filters.userType === 'maker' ? 'Maker' : 'Checker',
    onDelete: () => setFilters({
      ...filters,
      userType: 'all'
    })
  }] : []), ...(filters.dateFrom ? [{
    id: 'dateFrom',
    label: 'From',
    value: new Date(filters.dateFrom).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateFrom: ''
    })
  }] : []), ...(filters.dateTo ? [{
    id: 'dateTo',
    label: 'To',
    value: new Date(filters.dateTo).toLocaleDateString(),
    onDelete: () => setFilters({
      ...filters,
      dateTo: ''
    })
  }] : []), ...Object.entries(columnFilters).filter(([key, value]) => value !== 'all' && value !== '').map(([key, value]) => ({
    id: `column-${key}-${value}`,
    label: columns.find(col => col.id === key)?.label || key,
    value: typeof value === 'string' ? value : value.join(', '),
    onDelete: () => handleColumnFilterChange(key, ['status', 'userType', 'subAgent', 'branchCode'].includes(key) ? 'all' : '')
  }))];
  // Define columns
  const columns: Column<SubAgentRecord>[] = [{
    id: 'name',
    label: 'Name',
    renderFilter: () => <TextField size="small" placeholder="Search name" value={filters.name} onChange={e => handleTextFilterChange('name', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1963">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1964" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1962" />
  }, {
    id: 'fatherName',
    label: 'Department',
    renderFilter: () => <TextField size="small" placeholder="Search department" value={filters.fatherName} onChange={e => handleTextFilterChange('fatherName', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1966">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1967" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1965" />
  }, {
    id: 'cnic',
    label: 'Passport/ID',
    renderFilter: () => <TextField size="small" placeholder="Search Passport/ID" value={filters.cnic} onChange={e => handleTextFilterChange('cnic', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1969">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1970" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1968" />
  }, {
    id: 'email',
    label: 'Email',
    renderFilter: () => <TextField size="small" placeholder="Search email" value={filters.email} onChange={e => handleTextFilterChange('email', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1972">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1973" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1971" />
  }, {
    id: 'phone',
    label: 'Phone',
    renderFilter: () => <TextField size="small" placeholder="Search phone" value={filters.phone} onChange={e => handleTextFilterChange('phone', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1975">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1976" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1974" />
  }, {
    id: 'username',
    label: 'Username',
    renderFilter: () => <TextField size="small" placeholder="Search username" value={filters.username} onChange={e => handleTextFilterChange('username', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1978">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1979" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1977" />
  }, {
    id: 'branchCode',
    label: 'Branch Code',
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-1980">
          <Select value={columnFilters.branchCode || 'all'} onChange={e => handleColumnFilterChange('branchCode', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-1981">
            <MenuItem value="all" data-id="element-1982">All Branches</MenuItem>
            {BRANCH_CODES.map(code => <MenuItem key={code} value={code} data-id="element-1983">
                {code}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'settlementAccount',
    label: 'Settlement Account',
    renderFilter: () => <TextField size="small" placeholder="Search account" value={filters.settlementAccount} onChange={e => handleTextFilterChange('settlementAccount', e.target.value)} sx={{
      width: '100%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 1,
        fontSize: '0.8rem'
      }
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1985">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1986" />
              </InputAdornment>,
      sx: {
        height: 36
      }
    }} data-id="element-1984" />
  }, {
    id: 'subAgent',
    label: 'Sub Agent',
    renderCell: row => <Chip label={row.subAgent} size="small" sx={{
      backgroundColor: '#E0F7FF',
      color: '#0EA5E9',
      height: 24
    }} data-id="element-1987" />,
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-1988">
          <Select value={columnFilters.subAgent || 'all'} onChange={e => handleColumnFilterChange('subAgent', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-1989">
            <MenuItem value="all" data-id="element-1990">All Sub Agents</MenuItem>
            {SUB_AGENTS.map(agent => <MenuItem key={agent} value={agent} data-id="element-1991">
                {agent}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'userType',
    label: 'User Type',
    renderCell: row => <Chip label={row.userType === 'maker' ? 'Maker' : 'Checker'} size="small" sx={{
      backgroundColor: row.userType === 'maker' ? 'rgba(6, 182, 212, 0.1)' : 'rgba(139, 92, 246, 0.1)',
      color: row.userType === 'maker' ? '#06B6D4' : '#8B5CF6',
      height: 24,
      fontWeight: 600
    }} data-id="element-1992" />,
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-1993">
          <Select value={columnFilters.userType || 'all'} onChange={e => handleColumnFilterChange('userType', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-1994">
            <MenuItem value="all" data-id="element-1995">All Types</MenuItem>
            <MenuItem value="maker" data-id="element-1996">Maker</MenuItem>
            <MenuItem value="checker" data-id="element-1997">Checker</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => <Chip label={row.status === 'active' ? 'Active' : 'Inactive'} size="small" sx={{
      backgroundColor: row.status === 'active' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
      color: row.status === 'active' ? '#10B981' : '#EF4444',
      height: 24
    }} data-id="element-1998" />,
    renderFilter: () => <FormControl variant="outlined" size="small" fullWidth data-id="element-1999">
          <Select value={columnFilters.status || 'all'} onChange={e => handleColumnFilterChange('status', e.target.value)} displayEmpty sx={{
        borderRadius: 1,
        fontSize: '0.8rem',
        height: 36
      }} data-id="element-2000">
            <MenuItem value="all" data-id="element-2001">All Status</MenuItem>
            <MenuItem value="active" data-id="element-2002">Active</MenuItem>
            <MenuItem value="inactive" data-id="element-2003">Inactive</MenuItem>
          </Select>
        </FormControl>
  }];
  // Define row actions
  const rowActions: RowAction<SubAgentRecord>[] = [{
    icon: 'lucide:eye',
    label: 'View',
    onClick: record => console.log('View record', record)
  }, {
    icon: 'lucide:edit',
    label: 'Edit',
    onClick: onEditRecord
  }, {
    icon: 'lucide:mail',
    label: 'Resend Email',
    onClick: record => onResendEmail?.(record),
    condition: record => shouldShowResendEmail(record)
  }, {
    icon: 'lucide:x-circle',
    label: 'Deactivate',
    onClick: record => onStatusChange?.(record, 'inactive'),
    condition: record => record.status === 'active'
  }, {
    icon: 'lucide:check-circle',
    label: 'Activate',
    onClick: record => onStatusChange?.(record, 'active'),
    condition: record => record.status === 'inactive'
  }, {
    icon: 'lucide:key',
    label: 'Reset Password',
    onClick: record => onResetPassword?.(record)
  }, {
    icon: 'lucide:clock',
    label: 'Password Reset History',
    onClick: record => onViewResetHistory?.(record)
  }, {
    icon: 'lucide:history',
    label: 'Audit Log',
    onClick: record => console.log('View audit trail for record', record)
  }];
  // Handle export
  const handleExport = () => {
    console.log('Export data');
  };
  return <DataTable title="Sub Agent Records" data={filteredRecords} columns={columns} rowActions={rowActions} keyField="id" filterChips={filterChips} onClearAllFilters={handleClearFilters} loading={loading} initialVisibleColumns={['name', 'email', 'phone', 'username', 'branchCode', 'subAgent', 'userType', 'status']} onSearchChange={handleSearchChange} searchPlaceholder="Search records by name, email, phone, branch code..." searchValue={filters.search} onExport={handleExport} emptyStateMessage="No records found" data-id="element-2004" />;
};
export default SubAgentRecordsTable;